import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Copy, Sparkles, RefreshCw, Check, Plus, Trash2, Pencil, Save } from "lucide-react";
import api from "@/lib/api";
import EmptyState from "@/components/EmptyState";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const PLATFORMS = ["YouTube", "TikTok", "Instagram", "X (Twitter)"];

const TONES = [
  { value: "Kasual dan Humoris", label: "Kasual dan Humoris", desc: "Santai, akrab, sesekali menyelipkan humor." },
  { value: "Profesional dan Berbasis Data", label: "Profesional dan Berbasis Data", desc: "Objektif, terstruktur, mengutamakan fakta & bukti." },
  { value: "Energik dan Antusias", label: "Energik dan Antusias", desc: "Penuh semangat, optimistis, berenergi." },
  { value: "Ramah dan Suportif", label: "Ramah dan Suportif", desc: "Hangat, sabar, menyenangkan saat menjelaskan." },
  { value: "Kritis dan Analitis", label: "Kritis dan Analitis", desc: "Tajam mengurai masalah, tidak mudah menerima asumsi." },
  { value: "Ringkas dan To the Point", label: "Ringkas dan To the Point", desc: "Langsung ke inti, minim basa-basi, tetap jelas." },
];

const FIELDS = [
  { key: "niche", label: "Niche / topik utama kamu", placeholder: "cth: produktivitas buat mahasiswa, review gadget budget, masak simple" },
  { key: "audience", label: "Target audiens", placeholder: "cth: anak kuliahan 18-24, pekerja kantoran yang sibuk" },
  { key: "goal", label: "Tujuan konten", placeholder: "cth: bangun personal brand, jualan produk, edukasi" },
  { key: "unique", label: "Apa yang bikin kamu beda?", placeholder: "cth: pengalaman 5 tahun di industri, sudut pandang jujur tanpa gimmick" },
];

const EMPTY_IMG = "https://images.unsplash.com/photo-1761156254622-7b66649b1f69?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMHRlcnJhY290dGElMjBtaW5pbWFsaXN0fGVufDB8fHx8MTc4NzU5MjU3MHww&ixlib=rb-4.1.0&q=85";

const BLANK = { name: "", platform: "YouTube", niche: "", audience: "", tone: "Kasual dan Humoris", goal: "", unique: "" };

function DnaRender({ text }) {
  const lines = text.split("\n");
  return (
    <div className="space-y-2.5">
      {lines.map((line, i) => {
        const t = line.trim();
        if (!t) return <div key={i} className="h-1" />;
        if (t.startsWith("### ")) return <h4 key={i} className="font-bold text-sk-cream text-base mt-4">{inline(t.slice(4))}</h4>;
        if (t.startsWith("## ")) return <h3 key={i} className="font-extrabold text-sk-brand text-lg mt-5 tracking-tight">{inline(t.slice(3))}</h3>;
        if (t.startsWith("# ")) return <h2 key={i} className="font-extrabold text-sk-cream text-xl mt-5 tracking-tight">{inline(t.slice(2))}</h2>;
        if (/^[-*]\s/.test(t)) return <div key={i} className="flex gap-2.5 text-sk-muted text-sm leading-relaxed"><span className="text-sk-brand mt-0.5">•</span><span>{inline(t.replace(/^[-*]\s/, ""))}</span></div>;
        return <p key={i} className="text-sk-muted text-sm leading-relaxed">{inline(t)}</p>;
      })}
    </div>
  );
}

function inline(s) {
  const parts = s.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith("**") && p.endsWith("**")
      ? <strong key={i} className="text-sk-cream font-bold">{p.slice(2, -2)}</strong>
      : <span key={i}>{p}</span>
  );
}

export default function IdentitasKreator() {
  const [profiles, setProfiles] = useState([]);
  const [currentId, setCurrentId] = useState(null);
  const [answers, setAnswers] = useState(BLANK);
  const [dna, setDna] = useState("");
  const [loading, setLoading] = useState(false);
  const [initLoading, setInitLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  const [otherTone, setOtherTone] = useState(false);

  const loadProfiles = async () => {
    try {
      const res = await api.get("/identities");
      setProfiles(res.data);
      return res.data;
    } catch (e) { return []; }
  };

  useEffect(() => {
    loadProfiles().then((list) => {
      if (list.length > 0) selectProfile(list[0], list);
    }).finally(() => setInitLoading(false));
  }, []); // eslint-disable-line

  const selectProfile = (p, list) => {
    setCurrentId(p.id);
    setAnswers({ ...BLANK, ...(p.answers || {}), name: p.name || "" });
    setDna(p.dna || "");
    setEditing(false);
    setOtherTone(!!p.answers?.tone && !TONES.some((t) => t.value === p.answers.tone));
  };

  const newProfile = () => {
    setCurrentId(null);
    setAnswers(BLANK);
    setDna("");
    setEditing(false);
    setOtherTone(false);
  };

  const generate = async () => {
    if (!answers.niche.trim()) { toast.error("Isi dulu niche kamu ya biar hasilnya pas"); return; }
    setLoading(true);
    try {
      const res = await api.post("/identity/generate", answers);
      setDna(res.data.dna);
      setEditing(false);
      setCurrentId(res.data.id);
      setAnswers((a) => ({ ...a, name: res.data.name }));
      setProfiles((prev) => [res.data, ...prev]);
      toast.success("DNA Personal Brand kamu udah jadi & tersimpan!");
    } catch (e) {
      toast.error("Yah, ada gangguan pas nyambung ke AI. Coba lagi ya!");
    } finally {
      setLoading(false);
    }
  };

  const deleteProfile = async () => {
    if (!currentId) return;
    await api.delete(`/identities/${currentId}`);
    const remaining = profiles.filter((p) => p.id !== currentId);
    setProfiles(remaining);
    toast.success("Profil dihapus");
    if (remaining.length > 0) selectProfile(remaining[0]);
    else newProfile();
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(dna);
      setCopied(true);
      toast.success("Hasil disalin ke clipboard");
      setTimeout(() => setCopied(false), 1800);
    } catch (e) {
      toast.error("Gagal menyalin — coba salin manual ya");
    }
  };

  const setField = (k, v) => setAnswers((a) => ({ ...a, [k]: v }));

  const startEdit = () => { setEditText(dna); setEditing(true); };
  const cancelEdit = () => { setEditing(false); };
  const saveEdit = async () => {
    if (!currentId) return;
    setSavingEdit(true);
    try {
      await api.put(`/identities/${currentId}`, { dna: editText });
      setDna(editText);
      setProfiles((prev) => prev.map((p) => (p.id === currentId ? { ...p, dna: editText } : p)));
      setEditing(false);
      toast.success("Perubahan DNA tersimpan");
    } catch (e) {
      toast.error("Gagal menyimpan. Coba lagi ya!");
    } finally {
      setSavingEdit(false);
    }
  };

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-sk-brand font-semibold uppercase tracking-wide text-xs mb-2">Mulai di sini</p>
          <h1 className="font-extrabold text-3xl sm:text-4xl tracking-tight text-sk-cream">Identitas Kreator</h1>
          <p className="text-sk-muted mt-2 max-w-xl leading-relaxed">Jawab beberapa pertanyaan, dan biarkan AI meracik DNA Personal Brand kamu — jadi pondasi setiap konten.</p>
        </div>
        <div className="flex items-center gap-2">
          {profiles.length > 0 && (
            <Select value={currentId || ""} onValueChange={(id) => { const p = profiles.find((x) => x.id === id); if (p) selectProfile(p); }}>
              <SelectTrigger className="w-[180px] bg-sk-surface border-sk-line rounded-xl text-sk-cream" data-testid="profile-select">
                <SelectValue placeholder="Profil tersimpan" />
              </SelectTrigger>
              <SelectContent className="bg-sk-surface border-sk-line text-sk-cream">
                {profiles.map((p) => <SelectItem key={p.id} value={p.id} data-testid={`profile-opt-${p.id}`}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <button onClick={newProfile} data-testid="new-profile-btn" className="flex items-center gap-1.5 rounded-xl border border-sk-line bg-sk-surface px-3 py-2 text-sm font-semibold text-sk-cream hover:border-sk-brand hover:text-sk-brand transition-colors">
            <Plus className="h-4 w-4" /> Baru
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Form */}
        <div className="rounded-2xl border border-sk-line bg-sk-surface p-6 sm:p-8 shadow-sm">
          <div className="mb-5">
            <label className="block text-sm font-semibold text-sk-cream mb-2">Nama profil</label>
            <input
              data-testid="field-name"
              value={answers.name}
              onChange={(e) => setField("name", e.target.value)}
              placeholder="cth: Channel Produktivitas (opsional)"
              className="w-full rounded-xl bg-sk-bg border border-sk-line px-4 py-3 text-sm text-sk-cream placeholder:text-sk-muted/60 focus:outline-none focus:ring-2 focus:ring-sk-brand focus:border-transparent"
            />
          </div>

          <div className="mb-5">
            <label className="block text-sm font-semibold text-sk-cream mb-2.5">Platform utama</label>
            <div className="flex flex-wrap gap-2">
              {PLATFORMS.map((p) => (
                <button
                  key={p}
                  data-testid={`platform-${p}`}
                  onClick={() => setField("platform", p)}
                  className={`rounded-full px-4 py-2 text-sm font-medium border transition-colors ${
                    answers.platform === p
                      ? "bg-sk-brand text-sk-ink border-sk-brand"
                      : "border-sk-line text-sk-muted hover:text-sk-cream hover:border-sk-surface2"
                  }`}
                >{p}</button>
              ))}
            </div>
          </div>

          <div className="mb-5">
            <label className="block text-sm font-semibold text-sk-cream mb-2">Niche / topik utama kamu</label>
            <textarea data-testid="field-niche" value={answers.niche} onChange={(e) => setField("niche", e.target.value)} placeholder={FIELDS[0].placeholder} rows={2} className="w-full rounded-xl bg-sk-bg border border-sk-line px-4 py-3 text-sm text-sk-cream placeholder:text-sk-muted/60 resize-none focus:outline-none focus:ring-2 focus:ring-sk-brand focus:border-transparent" />
          </div>

          <div className="mb-5">
            <label className="block text-sm font-semibold text-sk-cream mb-2">Target audiens</label>
            <textarea data-testid="field-audience" value={answers.audience} onChange={(e) => setField("audience", e.target.value)} placeholder={FIELDS[1].placeholder} rows={2} className="w-full rounded-xl bg-sk-bg border border-sk-line px-4 py-3 text-sm text-sk-cream placeholder:text-sk-muted/60 resize-none focus:outline-none focus:ring-2 focus:ring-sk-brand focus:border-transparent" />
          </div>

          <div className="mb-5">
            <label className="block text-sm font-semibold text-sk-cream mb-2">Gaya bicara kamu</label>
            <Select value={otherTone ? "__other__" : answers.tone} onValueChange={(v) => { if (v === "__other__") { setOtherTone(true); setField("tone", ""); } else { setOtherTone(false); setField("tone", v); } }}>
              <SelectTrigger className="w-full bg-sk-bg border-sk-line rounded-xl text-sk-cream h-auto py-3" data-testid="field-tone">
                <SelectValue placeholder="Pilih gaya bicara" />
              </SelectTrigger>
              <SelectContent className="bg-sk-surface border-sk-line text-sk-cream max-w-[92vw]">
                {TONES.map((t) => (
                  <SelectItem key={t.value} value={t.value} data-testid={`tone-${t.value}`}>
                    <div className="flex flex-col text-left">
                      <span className="font-semibold">{t.label}</span>
                      <span className="text-xs text-sk-muted">{t.desc}</span>
                    </div>
                  </SelectItem>
                ))}
                <SelectItem value="__other__" data-testid="tone-other">
                  <div className="flex flex-col text-left">
                    <span className="font-semibold">Lainnya</span>
                    <span className="text-xs text-sk-muted">Tulis gaya bicara kamu sendiri.</span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
            {otherTone && (
              <input
                data-testid="field-tone-custom"
                value={answers.tone}
                onChange={(e) => setField("tone", e.target.value)}
                placeholder="cth: Sarkastik & blak-blakan, storytelling sinematik, dll"
                className="mt-2.5 w-full rounded-xl bg-sk-bg border border-sk-line px-4 py-3 text-sm text-sk-cream placeholder:text-sk-muted/60 focus:outline-none focus:ring-2 focus:ring-sk-brand focus:border-transparent"
              />
            )}
          </div>

          <div className="mb-5">
            <label className="block text-sm font-semibold text-sk-cream mb-2">Tujuan konten</label>
            <textarea data-testid="field-goal" value={answers.goal} onChange={(e) => setField("goal", e.target.value)} placeholder={FIELDS[2].placeholder} rows={2} className="w-full rounded-xl bg-sk-bg border border-sk-line px-4 py-3 text-sm text-sk-cream placeholder:text-sk-muted/60 resize-none focus:outline-none focus:ring-2 focus:ring-sk-brand focus:border-transparent" />
          </div>

          <div className="mb-5">
            <label className="block text-sm font-semibold text-sk-cream mb-2">Apa yang bikin kamu beda?</label>
            <textarea data-testid="field-unique" value={answers.unique} onChange={(e) => setField("unique", e.target.value)} placeholder={FIELDS[3].placeholder} rows={2} className="w-full rounded-xl bg-sk-bg border border-sk-line px-4 py-3 text-sm text-sk-cream placeholder:text-sk-muted/60 resize-none focus:outline-none focus:ring-2 focus:ring-sk-brand focus:border-transparent" />
          </div>

          <button
            onClick={generate}
            disabled={loading}
            data-testid="generate-identity-btn"
            className="w-full flex items-center justify-center gap-2 rounded-full bg-sk-brand text-sk-ink font-bold py-3.5 mt-2 transition-transform duration-200 hover:-translate-y-0.5 hover:bg-sk-brandDark disabled:opacity-60 disabled:hover:translate-y-0 shadow-lg shadow-sk-brand/20"
          >
            {loading ? <><RefreshCw className="h-4 w-4 animate-spin" /> Menyelaraskan dengan DNA brand kamu...</> : <><Sparkles className="h-4 w-4" /> Hasilkan Identitas Kreator</>}
          </button>
        </div>

        {/* Result */}
        <div className={`rounded-2xl border bg-sk-surface p-6 sm:p-8 shadow-sm ${loading ? "sk-generating" : "border-sk-line"}`}>
          {initLoading ? (
            <div className="flex items-center justify-center h-full py-14">
              <div className="h-8 w-8 rounded-full border-2 border-sk-line border-t-sk-brand animate-spin" />
            </div>
          ) : dna ? (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <div className="flex items-center justify-between mb-5 gap-2">
                <h2 className="font-extrabold text-lg text-sk-cream tracking-tight truncate">{answers.name || "DNA Personal Brand"}</h2>
                <div className="flex items-center gap-2 shrink-0">
                  {editing ? (
                    <>
                      <button onClick={cancelEdit} data-testid="cancel-edit-identity-btn" className="rounded-full border border-sk-line px-3 py-1.5 text-xs font-semibold text-sk-muted hover:text-sk-cream transition-colors">Batal</button>
                      <button onClick={saveEdit} disabled={savingEdit} data-testid="save-edit-identity-btn" className="flex items-center gap-1.5 rounded-full bg-sk-brand text-sk-ink px-3 py-1.5 text-xs font-bold hover:bg-sk-brandDark transition-colors disabled:opacity-60">
                        <Save className="h-3.5 w-3.5" /> {savingEdit ? "Menyimpan..." : "Simpan"}
                      </button>
                    </>
                  ) : (
                    <>
                      {currentId && (
                        <button onClick={startEdit} data-testid="edit-identity-btn" className="flex items-center gap-1.5 rounded-full border border-sk-line px-3 py-1.5 text-xs font-semibold text-sk-cream hover:border-sk-brand hover:text-sk-brand transition-colors">
                          <Pencil className="h-3.5 w-3.5" /> Edit
                        </button>
                      )}
                      <button onClick={copy} data-testid="copy-identity-btn" className="flex items-center gap-1.5 rounded-full border border-sk-line px-3 py-1.5 text-xs font-semibold text-sk-cream hover:border-sk-brand hover:text-sk-brand transition-colors">
                        {copied ? <><Check className="h-3.5 w-3.5" /> Tersalin</> : <><Copy className="h-3.5 w-3.5" /> Salin</>}
                      </button>
                      {currentId && (
                        <button onClick={deleteProfile} data-testid="delete-identity-btn" className="rounded-full border border-sk-line p-1.5 text-sk-muted hover:text-sk-brand hover:border-sk-brand transition-colors"><Trash2 className="h-3.5 w-3.5" /></button>
                      )}
                    </>
                  )}
                </div>
              </div>
              {editing ? (
                <>
                  <textarea
                    data-testid="edit-identity-textarea"
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    rows={18}
                    className="w-full rounded-xl bg-sk-bg border border-sk-line px-4 py-3 text-sm text-sk-cream leading-relaxed resize-y focus:outline-none focus:ring-2 focus:ring-sk-brand focus:border-transparent font-mono"
                  />
                  <p className="text-xs text-sk-muted mt-2">Kamu bisa sesuaikan isinya. Format Markdown (## judul, **tebal**, - poin) tetap didukung.</p>
                </>
              ) : (
                <DnaRender text={dna} />
              )}
            </motion.div>
          ) : (
            <EmptyState
              testid="identity-empty"
              image={EMPTY_IMG}
              title="DNA kamu belum diracik"
              subtitle="Isi asesmen di samping, lalu klik tombol. Hasilnya bakal jadi pondasi buat semua script kamu nanti."
            />
          )}
        </div>
      </div>
    </div>
  );
}
